import React, { useCallback, useEffect, useState } from 'react';
import { Switch, Button } from '@/components/ui';
import { FeatureGate } from '@/components/FeatureGate';
import { TierFeatureKey } from '@court-sync/shared';
import { Control, Controller } from 'react-hook-form';
import type { AcademyFormData } from '@court-sync/shared';
import { subscriptionSmsService, type SmsUsageResponse } from '@/services/subscriptionSms';
import { toast } from 'react-hot-toast';
import { AcademySettingsSection } from './AcademySettingsSection';

interface SmsCommunicationsSectionProps {
  control: Control<AcademyFormData>;
}

export function SmsCommunicationsSection({ control }: SmsCommunicationsSectionProps) {
  const [usage, setUsage] = useState<SmsUsageResponse | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [addonLoading, setAddonLoading] = useState(false);

  const loadUsage = useCallback(async () => {
    try {
      setLoadingUsage(true);
      const res = await subscriptionSmsService.getUsage();
      if (res.success && res.data) {
        setUsage(res.data);
      }
    } catch {
      // Tier may not include SMS — section is gated
    } finally {
      setLoadingUsage(false);
    }
  }, []);

  useEffect(() => {
    void loadUsage();
  }, [loadUsage]);

  const usagePercent =
    usage && usage.allowance > 0 ? Math.round((usage.sent / usage.allowance) * 100) : 0;
  const nearLimit = usagePercent >= 80;

  const handleEnableAddon = async () => {
    try {
      setAddonLoading(true);
      const res = await subscriptionSmsService.enableAddon();
      if (res.success) {
        toast.success('SMS add-on enabled');
        await loadUsage();
      } else {
        toast.error(res.error || 'Failed to enable add-on');
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to enable add-on');
    } finally {
      setAddonLoading(false);
    }
  };

  return (
    <FeatureGate feature={TierFeatureKey.SMS_NOTIFICATIONS} inline>
      <AcademySettingsSection
        title="SMS notifications"
        description="Send urgent transactional SMS to students and guardians who opt in per academy (lesson cancellations, postponements, waitlist alerts). Monthly allowance depends on your plan; optional add-on adds 500 more segments per month."
      >
        <div className="space-y-8">
          <div className="flex items-start gap-3">
            <Controller
              name="smsEnabled"
              control={control}
              render={({ field }) => (
                <Switch
                  id="sms-enabled"
                  checked={!!field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <label htmlFor="sms-enabled" className="cursor-pointer text-sm">
              <span className="font-medium text-gray-900 dark:text-white">
                Enable SMS for students and guardians
              </span>
              <p className="mt-1 text-gray-600 dark:text-gray-400">
                Students and guardians must still opt in with a phone number on their academy profile.
              </p>
            </label>
          </div>

          {!loadingUsage && usage ? (
            <div className="space-y-2 rounded-lg border border-border bg-muted/20 p-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Usage this period</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {usage.sent} / {usage.allowance} SMS
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-muted">
                <div
                  className={`h-full rounded-full transition-all ${
                    nearLimit ? 'bg-amber-500' : 'bg-primary'
                  }`}
                  style={{ width: `${Math.min(usagePercent, 100)}%` }}
                />
              </div>
              {nearLimit && !usage.addonEnabled ? (
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  You are approaching your monthly SMS allowance.
                </p>
              ) : null}
              {usage.addonEnabled ? (
                <p className="text-xs text-muted-foreground">SMS add-on (+500/mo) is active.</p>
              ) : null}
              {nearLimit && !usage.addonEnabled ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  isLoading={addonLoading}
                  onClick={() => void handleEnableAddon()}
                >
                  Add 500 SMS for{' '}
                  {usage.addonCurrency === 'AUD'
                    ? `A$${usage.addonMonthlyPrice}`
                    : `$${usage.addonMonthlyPrice}`}
                  /mo
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </AcademySettingsSection>
    </FeatureGate>
  );
}
